import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery,
  type SxProps,
  type Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";

export interface WizardStep {
  label: string;
  content: React.ReactNode;
}

interface WizardDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  steps: WizardStep[];
  onConfirm: () => void | Promise<void>;
  isConfirming?: boolean;
  canConfirm?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  sx?: SxProps<Theme>;
  disableFutureTabs?: boolean;
}

const WizardDialog: React.FC<WizardDialogProps> = ({
  open,
  onClose,
  title,
  steps,
  onConfirm,
  isConfirming = false,
  canConfirm = true,
  maxWidth = "lg",
  sx,
  disableFutureTabs = false,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!open) {
      setActiveStep(0);
    }
  }, [open]);

  const isLastStep = activeStep === steps.length - 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={isSmallScreen}
      sx={sx}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        {title}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ "&:focus": { outline: "none", boxShadow: "none" } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={activeStep}
            onChange={(_, v) => setActiveStep(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {steps.map((step, index) => (
              <Tab
                key={index}
                label={step.label}
                disabled={disableFutureTabs && index > activeStep}
              />
            ))}
          </Tabs>
        </Box>
        {steps[activeStep]?.content}
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={onClose}
            size="small"
            variant="outlined"
            sx={{ "&:focus": { outline: "none", boxShadow: "none" } }}
          >
            Cancelar
          </Button>
          {isLastStep ? (
            <Button
              onClick={async () => {
                try {
                  await onConfirm();
                } catch {
                  // El error queda en el estado de la mutación (isError/error) via TanStack Query
                }
              }}
              size="small"
              variant="contained"
              disabled={isConfirming || !canConfirm}
              sx={{ "&:focus": { outline: "none", boxShadow: "none" } }}
            >
              {isConfirming ? "Procesando..." : "Confirmar"}
            </Button>
          ) : (
            <Button
              onClick={() => setActiveStep(prev => prev + 1)}
              size="small"
              variant="contained"
              sx={{ "&:focus": { outline: "none", boxShadow: "none" } }}
            >
              Continuar
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default WizardDialog;
